const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://hess-communicator.onrender.com/api/v1').replace(/\/+$/, '')
const API_KEY = process.env.REACT_APP_API_KEY || process.env.REACT_APP_X_API_KEY || ''
const LOGIN_SESSION_KEY = 'cnergi-login-session'
const DEVICE_ASSIGNMENT_KEY = 'cnergi-device-assignment'

function isStandalonePwa() {
  if (typeof window === 'undefined') {
    return false
  }

  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true
}

async function parseResponse(response) {
  const text = await response.text()

  if (!text) {
    return null
  }

  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function getErrorMessage(data, fallback) {
  if (!data) {
    return fallback
  }

  if (typeof data === 'string') {
    return data
  }

  return data.message || data.error || fallback
}

export async function loginUser({ email, password }) {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify({ email, password }),
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to sign in. Please check your email and password.'))
  }

  return data
}

export async function registerUser({ firstName, middleName, lastName, email, password, contactNo }) {
  const response = await fetch(`${API_BASE_URL}/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify({
      firstName,
      middleName,
      lastName,
      email,
      password,
      contactNo,
      role: 'user',
    }),
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to create your account. Please try again.'))
  }

  return data
}

export async function getAllUsers() {
  const response = await fetch(`${API_BASE_URL}/users`, {
    method: 'GET',
    headers: {
      'x-api-key': API_KEY,
    },
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to fetch users.'))
  }

  if (Array.isArray(data?.users)) {
    return data.users
  }

  if (data?.users && typeof data.users === 'object') {
    return Object.values(data.users)
  }

  return []
}

export async function deleteUser(userId) {
  const response = await fetch(`${API_BASE_URL}/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: {
      'x-api-key': API_KEY,
    },
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to delete user.'))
  }

  return data
}

export async function updateUser(userId, updates) {
  const response = await fetch(`${API_BASE_URL}/users/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify(updates),
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to update user.'))
  }

  return data
}

export async function getUserProfile(email) {
  const response = await fetch(`${API_BASE_URL}/auth/profile/${encodeURIComponent(email)}`, {
    method: 'GET',
    headers: {
      'x-api-key': API_KEY,
    },
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to fetch user profile.'))
  }

  return data
}

export async function getBoardInfo(deviceId) {
  const response = await fetch(`${API_BASE_URL}/boards/${encodeURIComponent(deviceId)}/info`, {
    method: 'GET',
    headers: {
      'x-api-key': API_KEY,
    },
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to fetch board info.'))
  }

  return data
}

export async function getAllBoards() {
  const response = await fetch(`${API_BASE_URL}/boards/0/list`, {
    method: 'GET',
    headers: {
      'x-api-key': API_KEY,
    },
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to fetch boards.'))
  }

  if (Array.isArray(data?.data)) {
    return data.data
  }

  if (data?.data && typeof data.data === 'object') {
    return Object.entries(data.data).map(([deviceId, companyId]) => ({
      id: deviceId,
      deviceId,
      companyId,
    }))
  }

  return []
}

export async function deleteBoard(deviceId) {
  const response = await fetch(`${API_BASE_URL}/boards/${encodeURIComponent(deviceId)}`, {
    method: 'DELETE',
    headers: {
      'x-api-key': API_KEY,
    },
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to delete device.'))
  }

  return data
}

export async function getMeterModels() {
  const response = await fetch(`${API_BASE_URL}/boards/device/meter`, {
    method: 'GET',
    headers: {
      'x-api-key': API_KEY,
    },
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to fetch meter models.'))
  }

  return Array.isArray(data?.data) ? data.data.map((model) => String(model)).filter((model) => model !== '0') : []
}

export async function getInverterModes() {
  const response = await fetch(`${API_BASE_URL}/boards/device/inverter`, {
    method: 'GET',
    headers: {
      'x-api-key': API_KEY,
    },
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to fetch inverter modes.'))
  }

  return Array.isArray(data?.data) ? data.data.map((mode) => String(mode)).filter((mode) => mode !== '0') : []
}

export async function saveBoardConfig(deviceId, configuration) {
  console.log('[Board Config PUT]', {
    deviceId,
    url: `${API_BASE_URL}/boards/${encodeURIComponent(deviceId)}/config`,
    payload: configuration,
  })

  const response = await fetch(`${API_BASE_URL}/boards/${encodeURIComponent(deviceId)}/config`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify(configuration),
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to save the board configuration.'))
  }

  return data
}

export async function updateBoardRelays(deviceId, relays) {
  const response = await fetch(`${API_BASE_URL}/boards/${encodeURIComponent(deviceId)}/relay`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify({
      relay1: {
        state: relays.relay1.enabled ? '1' : '0',
        label: relays.relay1.label || 'Relay 1',
      },
      relay2: {
        state: relays.relay2.enabled ? '1' : '0',
        label: relays.relay2.label || 'Relay 2',
      },
    }),
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to update the relay controls.'))
  }

  return data
}

export async function updateUserDeviceIds(userId, deviceIds) {
  const response = await fetch(`${API_BASE_URL}/users/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify({
      deviceIds: deviceIds.join(', '),
    }),
  })
  const data = await parseResponse(response)

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, 'Unable to assign the device to the customer.'))
  }

  return data
}

export function getProfileDeviceIds(profileResponse) {
  const profileUser = profileResponse?.user || {}
  const deviceIds = profileUser.deviceIds
    ?? profileUser.deviceids
    ?? profileUser.deviceIDs
    ?? profileResponse?.deviceIds
    ?? profileResponse?.deviceids
    ?? profileResponse?.deviceIDs

  if (Array.isArray(deviceIds)) {
    return deviceIds.map((deviceId) => String(deviceId).trim()).filter(Boolean)
  }

  if (typeof deviceIds === 'string') {
    return deviceIds
      .split(',')
      .map((deviceId) => deviceId.trim())
      .filter(Boolean)
  }

  return deviceIds ? [String(deviceIds)] : []
}

export function getPrimaryProfileDeviceId(profileResponse) {
  return getProfileDeviceIds(profileResponse)[0] || ''
}

export function getUserDisplayInfo(user = {}) {
  const firstName = user.firstName || ''
  const lastName = user.lastName || ''
  const fullName = user.fullName || user.name || [firstName, lastName].filter(Boolean).join(' ')
  const displayName = fullName || 'User'

  return {
    firstName: firstName || displayName.split(/\s+/)[0] || 'User',
    lastName,
    email: user.email || '',
    displayName,
  }
}

export function getLoginUserRole(loginResponse) {
  const role = loginResponse?.user?.role
    ?? loginResponse?.data?.user?.role
    ?? loginResponse?.data?.role
    ?? loginResponse?.role

  return String(role || '').trim().toLowerCase()
}

const REMEMBERED_LOGIN_EMAIL_KEY = 'cnergi-remembered-login-email'

export function getRememberedLoginEmail() {
  if (typeof window === 'undefined') {
    return ''
  }

  return window.localStorage.getItem(REMEMBERED_LOGIN_EMAIL_KEY) || ''
}

export function saveRememberedLoginEmail(email, remember = false) {
  if (typeof window === 'undefined') {
    return
  }

  if (remember) {
    window.localStorage.setItem(REMEMBERED_LOGIN_EMAIL_KEY, String(email || '').trim())
    return
  }

  window.localStorage.removeItem(REMEMBERED_LOGIN_EMAIL_KEY)
}

export function saveLoginSession(loginResponse, remember = false) {
  if (typeof window === 'undefined') {
    return
  }

  const session = {
    user: loginResponse.user ?? loginResponse.data?.user ?? loginResponse.data ?? {},
    loginTime: loginResponse.loginTime,
  }
  const shouldPersist = remember || isStandalonePwa()
  const storage = shouldPersist ? window.localStorage : window.sessionStorage
  const otherStorage = shouldPersist ? window.sessionStorage : window.localStorage

  storage.setItem(LOGIN_SESSION_KEY, JSON.stringify(session))
  otherStorage.removeItem(LOGIN_SESSION_KEY)
}

export function getLoginSession() {
  if (typeof window === 'undefined') {
    return null
  }

  const storedLocalSession = window.localStorage.getItem(LOGIN_SESSION_KEY)
  const storedSessionSession = window.sessionStorage.getItem(LOGIN_SESSION_KEY)
  const storedSession = storedLocalSession || storedSessionSession

  if (!storedSession) {
    return null
  }

  try {
    const parsedSession = JSON.parse(storedSession)

    if (!storedLocalSession && storedSessionSession && isStandalonePwa()) {
      window.localStorage.setItem(LOGIN_SESSION_KEY, storedSessionSession)
      window.sessionStorage.removeItem(LOGIN_SESSION_KEY)
    }

    return parsedSession
  } catch {
    return null
  }
}

export function clearLoginSession() {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.removeItem(LOGIN_SESSION_KEY)
  window.sessionStorage.removeItem(LOGIN_SESSION_KEY)
}

export function savePendingDeviceAssignment(customer) {
  if (typeof window === 'undefined') {
    return
  }

  window.sessionStorage.setItem(DEVICE_ASSIGNMENT_KEY, JSON.stringify({
    userId: customer.userId,
    email: customer.email || '',
    displayName: customer.displayName || customer.email || 'Customer',
    deviceIds: Array.from(new Set((customer.deviceIds || []).map((deviceId) => String(deviceId).trim()).filter(Boolean))),
    technicianAuthorized: false,
  }))
}

export function authorizePendingDeviceAssignment() {
  const assignment = getPendingDeviceAssignment()

  if (!assignment || typeof window === 'undefined') {
    return
  }

  window.sessionStorage.setItem(DEVICE_ASSIGNMENT_KEY, JSON.stringify({
    ...assignment,
    technicianAuthorized: true,
  }))
}

export function getPendingDeviceAssignment() {
  if (typeof window === 'undefined') {
    return null
  }

  const storedAssignment = window.sessionStorage.getItem(DEVICE_ASSIGNMENT_KEY)

  if (!storedAssignment) {
    return null
  }

  try {
    return JSON.parse(storedAssignment)
  } catch {
    return null
  }
}

export function clearPendingDeviceAssignment() {
  if (typeof window === 'undefined') {
    return
  }

  window.sessionStorage.removeItem(DEVICE_ASSIGNMENT_KEY)
}
